import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import {
  Home,
  Boxes,
  ShoppingBag,
  Upload as UploadIcon,
  Sliders,
  FileText,
  ShieldCheck,
  User,
  LogOut,
  Search,
  Bell,
  Sparkles,
  Check,
} from "lucide-react";
import { signOutRequest } from "../../redux/auth/auth.actions";
import {
  DASHBOARD,
  BATCHES,
  EBAY_SYNC,
  UPLOAD,
  GLOBAL_PRESETS,
  TITLE_BUILDER,
  POLICIES,
  PROFILE_SETTINGS,
} from "../../routes/routes";

const NAV_ITEMS = [
  { path: DASHBOARD, label: "Dashboard", icon: Home },
  { path: BATCHES, label: "Batches", icon: Boxes },
  { path: EBAY_SYNC, label: "eBay Sync", icon: ShoppingBag },
  { path: UPLOAD, label: "Upload", icon: UploadIcon },
  { path: GLOBAL_PRESETS, label: "Global Presets", icon: Sliders },
  { path: TITLE_BUILDER, label: "Default Title Format", icon: FileText },
  { path: POLICIES, label: "Policies & Tax", icon: ShieldCheck },
  { path: PROFILE_SETTINGS, label: "Profile Settings", icon: User },
];

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const driftOne = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(220px, 140px) scale(1.15);
  }
  66% {
    transform: translate(80px, 260px) scale(0.9);
  }
  100% {
    transform: translate(0, 0) scale(1);
  }
`;

const driftTwo = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(-180px, -120px) scale(0.9);
  }
  66% {
    transform: translate(-60px, -220px) scale(1.2);
  }
  100% {
    transform: translate(0, 0) scale(1);
  }
`;

const Outer = styled.div`
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    #dfe4fb 0%,
    #eef0f4 25%,
    #f7e4f3 50%,
    #eef0f4 75%,
    #dfe4fb 100%
  );
  background-size: 250% 250%;
  animation: ${gradientShift} 18s ease-in-out infinite;
  padding: 1.5rem;
  font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    z-index: 0;
    will-change: transform;
  }

  &::before {
    width: 420px;
    height: 420px;
    top: -120px;
    left: -100px;
    background: rgba(109, 94, 248, 0.35);
    animation: ${driftOne} 22s ease-in-out infinite;
  }

  &::after {
    width: 380px;
    height: 380px;
    bottom: -140px;
    right: -80px;
    background: rgba(255, 133, 198, 0.3);
    animation: ${driftTwo} 26s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const Page = styled.div`
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 3rem);
  display: flex;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 8px 32px rgba(16, 24, 40, 0.1);
`;

const Sidebar = styled.aside`
  position: relative;
  width: 240px;
  flex-shrink: 0;
  background: linear-gradient(
    165deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.22) 60%,
    rgba(255, 255, 255, 0.12) 100%
  );
  backdrop-filter: blur(30px) saturate(200%);
  -webkit-backdrop-filter: blur(30px) saturate(200%);
  border-right: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.6),
    4px 0 24px rgba(31, 38, 135, 0.08);
  display: flex;
  flex-direction: column;
  padding: 1.25rem 0.75rem;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -40%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0) 60%
    );
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem 1.5rem;
`;

const BrandIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6d5ef8, #8b7bff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const BrandName = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a2e;
  white-space: nowrap;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const NavItem = styled(Link)`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.5)" : "transparent")};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(109, 94, 248, 0.85), rgba(139, 123, 255, 0.75))"
      : "transparent"};
  backdrop-filter: ${({ $active }) => ($active ? "blur(10px)" : "none")};
  -webkit-backdrop-filter: ${({ $active }) => ($active ? "blur(10px)" : "none")};
  box-shadow: ${({ $active }) => ($active ? "0 4px 16px rgba(109, 94, 248, 0.35)" : "none")};
  color: ${({ $active }) => ($active ? "#fff" : "#6b7280")};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(135deg, rgba(109, 94, 248, 0.85), rgba(139, 123, 255, 0.75))"
        : "rgba(109, 94, 248, 0.14)"};
    border-color: ${({ $active }) =>
      $active ? "rgba(255, 255, 255, 0.5)" : "rgba(109, 94, 248, 0.18)"};
    color: ${({ $active }) => ($active ? "#fff" : "#4b3fd6")};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.5rem;
  border-top: 1px solid rgba(17, 24, 39, 0.08);
  margin-top: 0.5rem;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #6d5ef8, #8b7bff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
    color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: calc(100vh - 3rem);
  overflow: hidden;
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 2;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.5rem;
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.4);
`;

const Breadcrumb = styled.div`
  font-size: 0.85rem;
  color: #9ca3af;

  span {
    color: #1a1a2e;
    font-weight: 500;
  }
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
  color: #9ca3af;
  font-size: 0.85rem;
  min-width: 220px;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InsightButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #6d5ef8, #8b7bff);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.25rem;
`;

const PageSubtitle = styled.p`
  font-size: 0.9rem;
  color: #9ca3af;
  margin: 0 0 1.75rem;
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  max-width: 480px;
  margin: 0 0 1.75rem;
`;

const StepCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $state }) =>
    $state === "done" ? "#6d5ef8" : $state === "current" ? "#fff" : "#fff"};
  border: 2px solid ${({ $state }) => ($state === "upcoming" ? "#e5e7eb" : "#6d5ef8")};
  color: ${({ $state }) => ($state === "done" ? "#fff" : "#6d5ef8")};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StepInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6d5ef8;
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ $filled }) => ($filled ? "#6d5ef8" : "#e5e7eb")};
`;

const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
`;

const StepLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $muted }) => ($muted ? "#9ca3af" : "#1a1a2e")};
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  padding: 1.75rem;
  max-width: 620px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
`;

const CardTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 1.25rem;
`;

const Field = styled.div`
  margin-bottom: 1.1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.4rem;

  em {
    color: #ef4444;
    font-style: normal;
    margin-left: 0.15rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(17, 24, 39, 0.1);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-size: 0.85rem;
  color: #1a1a2e;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #8a8d99;
  }

  &:focus {
    border-color: #6d5ef8;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(17, 24, 39, 0.1);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-size: 0.85rem;
  color: #1a1a2e;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;

  &::placeholder {
    color: #8a8d99;
  }

  &:focus {
    border-color: #6d5ef8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(17, 24, 39, 0.1);
  font-size: 0.85rem;
  color: #1a1a2e;
  outline: none;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  appearance: none;

  &:focus {
    border-color: #6d5ef8;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const CancelButton = styled.button`
  padding: 0.6rem 1.4rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
`;

const NextButton = styled.button`
  padding: 0.6rem 1.6rem;
  border-radius: 8px;
  border: none;
  background: #6d5ef8;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
`;

function FlowMailDashboard() {
  const location = useLocation();
  const dispatch = useDispatch();
  const adminData = useSelector((state) => state.auth?.admin_data);
  const [formName, setFormName] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState("");

  const isActive = (path) => {
    if (path === BATCHES) {
      return (
        location.pathname === path ||
        location.pathname.startsWith("/batches/") ||
        location.pathname.includes("/batches/")
      );
    }
    if (path === EBAY_SYNC) {
      return location.pathname === path || location.pathname.startsWith("/ebay-sync/");
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    dispatch(signOutRequest({ func: () => {} }));
  };

  const userInitial = (adminData?.username || "U").charAt(0).toUpperCase();

  return (
    <Outer>
      <Page>
        <Sidebar>
          <Brand>
            <BrandIcon>SC</BrandIcon>
            <BrandName>Sticker Checker</BrandName>
          </Brand>

          <Nav>
            {NAV_ITEMS.map((item) => (
              <NavItem key={item.path} to={item.path} $active={isActive(item.path)}>
                <item.icon size={18} />
                {item.label}
              </NavItem>
            ))}
          </Nav>

          <UserCard>
            <Avatar>{userInitial}</Avatar>
            <UserInfo>
              <UserName>{adminData?.username || "User"}</UserName>
              <UserEmail>{adminData?.email || ""}</UserEmail>
            </UserInfo>
            <LogoutButton type="button" onClick={handleLogout} aria-label="Logout">
              <LogOut />
            </LogoutButton>
          </UserCard>
        </Sidebar>

        <Main>
          <TopBar>
            <Breadcrumb>
              Forms / <span>Create New Form</span>
            </Breadcrumb>

            <TopBarActions>
              <SearchBox>
                <Search />
                Search here...
              </SearchBox>
              <IconButton type="button" aria-label="Notifications">
                <Bell />
              </IconButton>
              <InsightButton type="button">
                <Sparkles />
                Get AI Insight
              </InsightButton>
            </TopBarActions>
          </TopBar>

          <Content>
            <PageTitle>Create New Form</PageTitle>
            <PageSubtitle>Build a custom lead capture form for your website</PageSubtitle>

            <Steps>
              <StepColumn>
                <StepCircle $state="done">
                  <Check />
                </StepCircle>
                <StepLabel>Step 1</StepLabel>
              </StepColumn>
              <StepLine $filled />
              <StepColumn>
                <StepCircle $state="current">
                  <StepInner />
                </StepCircle>
                <StepLabel>Step 2</StepLabel>
              </StepColumn>
              <StepLine $filled={false} />
              <StepColumn>
                <StepCircle $state="upcoming" />
                <StepLabel $muted>Final</StepLabel>
              </StepColumn>
            </Steps>

            <Card>
              <CardTitle>Form Details</CardTitle>

              <Field>
                <Label htmlFor="formName">
                  Form Name<em>*</em>
                </Label>
                <Input
                  id="formName"
                  placeholder="e.g., Newsletter Signup"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="formDescription">Description</Label>
                <Textarea
                  id="formDescription"
                  placeholder="Brief description of this form's purpose"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="targetAudience">
                  Target Audience<em>*</em>
                </Label>
                <Select
                  id="targetAudience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  <option value="">Select Audience</option>
                  <option value="all">All Subscribers</option>
                  <option value="new">New Leads</option>
                  <option value="customers">Existing Customers</option>
                </Select>
              </Field>

              <Actions>
                <CancelButton type="button">Cancel</CancelButton>
                <NextButton type="button">Next</NextButton>
              </Actions>
            </Card>
          </Content>
        </Main>
      </Page>
    </Outer>
  );
}

export default FlowMailDashboard;
